import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { useContext, useEffect } from "react";
import {
  GoogleReCaptchaProvider,
  useGoogleReCaptcha,
} from "react-google-recaptcha-v3";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { useError } from "src/core/hooks/useError";
import { textService } from "src/core/services/textService";
import {
  BirthDateField,
  type BirthDateFormData,
} from "src/features/shared/components/BirthDateField";
import {
  EmailField,
  type EmailFormData,
} from "src/features/shared/components/EmailField";
import {
  GenderField,
  type GenderFormData,
} from "src/features/shared/components/GenderField";
import {
  NameField,
  type NameFormData,
} from "src/features/shared/components/NameField";
import {
  Password2Field,
  type Password2FormData,
} from "src/features/shared/components/Password2Field";
import {
  PasswordField,
  type PasswordFormData,
} from "src/features/shared/components/PasswordField";
import {
  PhoneField,
  type PhoneFormData,
} from "src/features/shared/components/PhoneField";
import {
  PrivacyAcceptField,
  type PrivacyAcceptFormData,
} from "src/features/shared/components/PrivacyAcceptField";
import {
  SSNField,
  type SSNFormData,
} from "src/features/shared/components/SSNField";
import { type RegisterRequest } from "../../../../core/backend/clients/backendClient";
import { ToastContext } from "../../../../core/context/ToastContext";
import { useErrorHandler } from "../../../../core/error/useErrorHandler";
import { logService } from "../../../../core/services/logService";
import { useSettingsStore } from "../../../settings/services/useSettingsStore";
import {
  AddressField,
  type AddressFormData,
} from "../../../shared/components/AddressField";
import { useProfile } from "../../hooks/useProfile";
import { useAuthStore } from "../../services/useAuthStore";
import "./RegisterPage.css";

export interface RegisterFormData
  extends NameFormData,
    EmailFormData,
    PasswordFormData,
    Password2FormData,
    BirthDateFormData,
    GenderFormData,
    SSNFormData,
    AddressFormData,
    PhoneFormData,
    PrivacyAcceptFormData {}

export function RegisterPage() {
  return (
    <GoogleReCaptchaProvider reCaptchaKey="6LdocKsZAAAAAEUb2Agh3GFAKQKRMGzSkDQY-dyP">
      <RegisterPageContent />
    </GoogleReCaptchaProvider>
  );
}

function RegisterPageContent() {
  const toastRef = useContext(ToastContext);

  const { t } = useTranslation();
  const navigate = useNavigate();
  const { birthDateLabel, ssnLabel, addressLabel, phoneLabel } = useProfile();
  const { getErrorMessage } = useError();

  const authStore = useAuthStore();
  const settingsStore = useSettingsStore();
  const errorHandler = useErrorHandler();

  const { executeRecaptcha } = useGoogleReCaptcha();

  const form = useForm<RegisterFormData>({
    mode: "all",
  });

  useEffect(() => {
    form.reset({
      name: "",
      email: "",
      password: "",
      password2: "",
      birthDate: undefined,
      gender: undefined,
      ssn: undefined,
      address: {
        postalCode: undefined,
        city: "",
        streetAddress: "",
      },
      phone: !!settingsStore.legacySettings.phoneRequired
        ? textService.formatPhone("+36")
        : "",
      dataPrivacyNoticeAccepted: false,
    });
  }, []);

  const onRegister = async () => {
    if (!form.formState.isValid) return;

    let recaptchaToken = "";
    if (executeRecaptcha) {
      recaptchaToken = await executeRecaptcha("register");
    }
    if (!recaptchaToken) {
      logService.error("RegisterPage.onRegister recaptchaToken is empty");
      errorHandler.setError({
        title: t("Home.Registration.Title"),
        message: t("Home.Registration.InvalidRecaptcha"),
      });
      return;
    }

    const formValues = form.getValues();

    logService.log("RegisterPage.onRegister", formValues);

    const request = { ...formValues } as RegisterRequest;

    await authStore
      .register(request)
      .then(() => {
        toastRef?.current?.show({
          severity: "success",
          summary: t("Home.Registration.Title"),
          detail: t("Home.Registration.Success"),
          closable: true,
          life: 15000,
        });
        navigate("/");
      })
      .catch((error) => {
        errorHandler.setError({
          title: t("Home.Registration.Title"),
          message: t("Home.Registration.Error") + " " + getErrorMessage(error),
        });
      });
  };

  return (
    <form>
      <div className="registerPage flex justify-content-center">
        <Card
          title={t("Home.Registration.Title")}
          className="card"
          footer={
            <div className="grid">
              <span className="col-4 hidden md:block"></span>
              <div className="col-12 md:col-8 flex justify-content-between gap-1">
                <Button
                  type="button"
                  label={t("Home.Registration.Title")}
                  icon="pi pi-check"
                  severity="success"
                  disabled={!form.formState.isValid}
                  loading={authStore.loading}
                  onClick={onRegister}
                ></Button>

                <Button
                  type="button"
                  label={t("Base.Cancel")}
                  icon="pi pi-times"
                  severity="warning"
                  onClick={() => navigate("/")}
                ></Button>
              </div>
            </div>
          }
        >
          <div className="field grid">
            <label
              htmlFor="name"
              className="col-12 md:col-4 font-bold"
            >
              {t("Home.Registration.Name")} *:
            </label>
            <div className="col-12 md:col-8">
              <NameField
                form={form}
                required={true}
              />
            </div>
          </div>

          <div className="field grid">
            <label
              htmlFor="email"
              className="col-12 md:col-4 font-bold"
            >
              {t("Home.Login.Email")} *:
            </label>
            <div className="col-12 md:col-8">
              <EmailField
                form={form}
                required={true}
              />
            </div>
          </div>

          <div className="field grid">
            <label
              htmlFor="password"
              className="col-12 md:col-4 font-bold"
            >
              {t("Home.Login.Password")} *:
            </label>
            <div className="col-12 md:col-8">
              <PasswordField
                form={form}
                required={true}
                feedback={true}
              />
            </div>
          </div>

          <div className="field grid">
            <label
              htmlFor="password2"
              className="col-12 md:col-4 font-bold white-space-nowrap"
            >
              {t("Home.Registration.Password2")} *:
            </label>
            <div className="col-12 md:col-8">
              <Password2Field
                form={form}
                required={true}
              />
            </div>
          </div>

          <div className="field grid">
            <label
              htmlFor="birthDate"
              className="col-12 md:col-4 font-bold"
            >
              {birthDateLabel} :
            </label>
            <div className="col-12 md:col-8">
              <BirthDateField
                form={form}
                required={
                  settingsStore.legacySettings.birthDateRequired ?? false
                }
              />
            </div>
          </div>

          <div className="field grid">
            <label className="col-12 md:col-4 font-bold">
              {t("Home.Registration.Gender")} *:
            </label>
            <div className="col-12 md:col-8 flex gap-2">
              <GenderField
                form={form}
                required={true}
              />
            </div>
          </div>

          <div className="field grid">
            <label
              htmlFor="ssn"
              className="col-12 md:col-4 font-bold"
            >
              {ssnLabel} :
            </label>
            <div className="col-12 md:col-8">
              <SSNField
                form={form}
                required={settingsStore.legacySettings.ssnRequired ?? false}
              />
            </div>
          </div>

          <div className="field grid">
            <label className="col-12 font-bold text-center">
              {t("Home.Registration.SSNInfo")}
            </label>
          </div>

          <div className="field grid">
            <label className="col-12 md:col-4 font-bold">
              {addressLabel} :
            </label>
            <div className="col-12 md:col-8">
              <AddressField
                form={form}
                required={settingsStore.legacySettings.addressRequired ?? false}
              />
            </div>
          </div>

          <div className="field grid">
            <label
              htmlFor="phone"
              className="col-12 md:col-4 font-bold"
            >
              {phoneLabel} :
            </label>
            <div className="col-12 md:col-8">
              <PhoneField
                form={form}
                required={settingsStore.legacySettings.phoneRequired ?? false}
              />
            </div>
          </div>

          <PrivacyAcceptField
            form={form}
            required={true}
          />
        </Card>
      </div>
    </form>
  );
}
